import angular from 'angular';

import * as actions from '../../store/actions';

const mapStateToScope = (state) => ({
  subComponent: state.statsbar.activeComponent,
});

/**
 * @ngdoc function
 * @name myConsultApp.controller:manageRewardList
 * @description
 * # manageRewardList
 * Controller of the myConsultApp
 */
angular
  .module('myConsultApp')
  .controller(
    'manageRewardList',
    ($ngRedux, $scope, $rootScope, $location, $route, $timeout, $http, ENV) => {
      if (!$scope.isSuperUser) {
        $location.path('/');
      }

      $scope.processParams = () =>
        new Promise((resolve) => {
          const { rewardId } = $route.current.params;
          $scope.takeAction(rewardId);
          resolve(true);
        });

      $scope.takeAction = (id) => {
        if (!id) return;

        if (id === 'create-reward') {
          $scope.open(id);
        } else if (id) {
          $scope
            .getReward($http, ENV, id)
            .then(() => {
              $scope.open(id);
            })
            .catch(() => {
              const errorModalOptions = {
                heading: 'Reward Error',
                message: `${id} is not available at this time.  It either does not exist or has caused an error.  Please try again later or contact support if the error persists.`,
                buttons: [
                  {
                    label: 'Close',
                    href: '#',
                    clickFn: () => {
                      $rootScope.toggleErrorModal();
                      $location.path($rootScope.routes.reward, false);
                    },
                  },
                ],
              };
              $rootScope.toggleErrorModal(errorModalOptions);
            });
        }
      };

      $scope.open = (id) => {
        const component = id === 'create-reward' ? 'create-reward' : 'edit-reward';
        const closeRoute = $rootScope.previousRoute || $rootScope.routes.reward;
        const beforeRoute = `${$rootScope.routes.reward}/${id}`;
        $rootScope.openComponent({
          component: component,
          beforeRoute: beforeRoute,
          closeRoute: closeRoute,
        });
      };

      $scope.init = () => {
        $rootScope.previousRoute = $rootScope.routes.reward;
        $scope.unsubscribe = $ngRedux.connect(mapStateToScope, actions)($scope);
        $scope.processParams().then(() => {
          $location.search({});
        });
      };

      $scope.table = {
        name: 'Manage Rewards',
        options: {
          endpoint: '/2.0/rewards/',
          endpoint_data_key: 'rewards',
          per_page_options: [15, 25, 50, 100, 200],
          records_per_page: 15,
          row_style: 'standard',
          scale_up: false,
          search_key: 'Rewards',
          search_required: false,
        },
        columns: [
          {
            name: 'Title',
            options: {
              content: '{{ record.title }}',
              class: 'medium-width',
            },
          },
          {
            name: 'Description',
            options: {
              content: '<div>{{ record.description }}</div>',
              class: 'medium-width',
            },
          },
          {
            name: 'Point Cost',
            options: {
              content: '{{ record.point_cost || 0 }}',
            },
          },
          {
            name: 'Reward IDs',
            options: {
              content: `
                <div>
                    <div>
                        <div class="table-row-label--bold">Atlas ID:</div>
                        <div>{{ record.id }}</div>
                    </div>
                    <div ng-if="record.contentstack_uid">
                        <div class="table-row-label--bold">Contentstack UID:</div>
                        <div>{{ record.contentstack_uid }}</div>
                    </div>
                    <div ng-if="record.parent_contentstack_uid">
                        <div class="table-row-label--bold">Contentstack Parent UID:</div>
                        <div>{{ record.parent_contentstack_uid }}</div>
                    </div>
                    <div ng-if="record.sku">
                        <div class="table-row-label--bold">SKU:</div>
                        <div>{{ record.sku}}</div>
                    </div>
                </div>`,
              class: 'small-width',
            },
          },
          {
            name: 'Created',
            options: {
              content: '{{ record.created }}',
            },
          },
          {
            name: 'Status/Inventory',
            options: {
              content: `
                <div>
                    <div ng-if="record.enabled" class="table-row-label--bold">Enabled</div>   
                    <div ng-if="!record.enabled" class="table-row-label--bold">Disabled</div>   
                    <div ng-if="record.inventory_options.inventory_count_available !== null">
                        <div class="table-row-label--bold">Total Inventory:</div>
                        <div>{{record.inventory_options.inventory_count_available}}</div>
                    </div>
                    <div ng-if="record.inventory_options.inventory_count_to_reserve !== null">
                        <div class="table-row-label--bold">Inventory to Reserve:</div>
                        <div>{{record.inventory_options.inventory_count_to_reserve}}</div>
                    </div>
                    <div ng-if="record.inventory_options.order_count !== null">
                        <div class="table-row-label--bold">Total Orders:</div>
                        <div>{{record.inventory_options.order_count}}</div>
                    </div>
                </div>`,
              class: 'small-width',
            },
          },
          {
            name: 'Restrictions',
            options: {
              content: `
                <div>
                    <div ng-if="record.quantity_restrictions.allow_quantity_selection" class="table-row-label--bold">Allow Quantity Selection</div>   
                    <div ng-if="record.require_address_to_order" class="table-row-label--bold">Require User Address</div>   
                    <div>
                        <div class="table-row-label--bold">Minimum Quantity Required</div>
                        <div>{{record.quantity_restrictions.minimum_quantity_required}}</div>
                    </div>
                    <div>
                        <div class="table-row-label--bold">Maximum Quantity Allowed:</div>
                        <div>{{record.quantity_restrictions.maximum_quantity_allowed}}</div>
                    </div>
                    <div ng-if="record.minimum_tier_availability.title">
                        <div class="table-row-label--bold">Minimum Tier Availability:</div>
                        <div>{{record.minimum_tier_availability.title}}</div>
                    </div>
                </div>`,
              class: 'small-width',
            },
          },
        ],
        filters: [
          {
            name: 'Reward Status',
            type: 'multiple_choice',
            options: {
              choices: {
                Active: { state: true, value: true, order: 0 },
                Inactive: { state: false, value: false, order: 1 },
              },
              class: 'application-filter',
              query_string_parameter: 'enabled',
            },
          },
          {
            name: 'Available for Order',
            type: 'multiple_choice',
            options: {
              choices: {
                Available: { state: true, value: true, order: 0 },
                Unavailable: { state: false, value: false, order: 1 },
              },
              class: 'application-filter',
              query_string_parameter: 'available_for_order',
            },
          },
        ],
        sort_options: {
          title: {
            state: true,
            value: '1',
            title: 'Title',
            order: 0,
          },
          parent_contentstack_uid: {
            state: false,
            value: '1',
            title: 'Contentstack Parent UID',
            order: 1,
          },
          created: {
            state: false,
            value: '-1',
            title: 'Reward Created',
            order: 2,
          },
          contentstack_uid: {
            state: false,
            value: '1',
            title: 'Contentstack UID',
            order: 3,
          },
          sku: {
            state: false,
            value: '1',
            title: 'SKU',
            order: 4,
          },
          point_cost: {
            state: false,
            value: '1',
            title: 'Point Cost',
            order: 5,
          },
        },
      };

      $scope.init();

      $scope.$on('$destroy', $scope.unsubscribe);
    },
  );
